import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import PrimaryNav from '../components/PrimaryNav';
import LeaderboardRewardsPanel from './LeaderboardRewardsPanel';

const PERIOD_TABS = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'all_time', label: 'All Time' },
];

export default function LeaderboardPage() {
  const [activePeriod, setActivePeriod] = useState('today');
  const [leaderboard, setLeaderboard] = useState([]);
  const [yourPosition, setYourPosition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchLeaderboard = useCallback(async () => {
    try {
      setError(null);
      const res = await api.get(`/leaderboards/whot?period=${encodeURIComponent(activePeriod)}`);
      setLeaderboard(res.data.leaderboard || []);
      setYourPosition(res.data.yourPosition || null);
    } catch (err) {
      console.error('Whot leaderboard fetch error:', err);
      setError(err.response?.data?.error || 'Failed to load Whot leaderboard');
    } finally {
      setLoading(false);
    }
  }, [activePeriod]);

  useEffect(() => {
    setLoading(true);
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  useEffect(() => {
    const interval = setInterval(fetchLeaderboard, 30000);
    return () => clearInterval(interval);
  }, [fetchLeaderboard]);

  const rankStyle = (rank) => {
    if (rank === 1) return { background: 'rgba(209, 168, 58, 0.10)', border: '1px solid rgba(209, 168, 58, 0.35)', number: '#d1a83a' };
    if (rank === 2) return { background: 'rgba(67, 88, 101, 0.18)', border: '1px solid rgba(67, 88, 101, 0.55)', number: '#b8c0c7' };
    if (rank === 3) return { background: 'rgba(67, 88, 101, 0.14)', border: '1px solid rgba(67, 88, 101, 0.48)', number: '#9da7ae' };
    return { background: 'linear-gradient(145deg, #121c24 0%, #0e161d 100%)', border: '1px solid rgba(67, 88, 101, 0.66)', number: '#9da7ae' };
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', color: '#fff', padding: '16px', boxSizing: 'border-box' }}>
      <div style={{ maxWidth: '680px', margin: '0 auto' }}>
        <LeaderboardRewardsPanel />
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '18px' }}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{ background: 'transparent', border: 0, color: '#00d4aa', fontSize: '15px', cursor: 'pointer', padding: '8px 12px 8px 0' }}
          >
            ← Back
          </button>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.45rem', color: '#fff' }}>🏆 WHOT LEADERBOARD</h1>
            <div style={{ marginTop: '3px', color: '#9aa3ab', fontSize: '12px' }}>Verified Whot Stake wins</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px', marginBottom: '18px' }}>
          {PERIOD_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActivePeriod(tab.key)}
              aria-pressed={activePeriod === tab.key}
              style={{
                padding: '10px 5px',
                borderRadius: '9px',
                border: activePeriod === tab.key ? '1px solid #00d4aa' : '1px solid rgba(67, 88, 101, 0.66)',
                background: activePeriod === tab.key ? '#00d4aa' : '#111616',
                color: activePeriod === tab.key ? '#06110f' : '#b8c0c7',
                fontWeight: '700',
                fontSize: '12px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#8d969d' }}>
            Loading leaderboard...
          </div>
        )}

        {!loading && error && (
          <div style={{ border: '1px solid rgba(255, 77, 77, 0.35)', background: 'rgba(255, 77, 77, 0.10)', color: '#fca5a5', padding: '18px', borderRadius: '12px', textAlign: 'center' }}>
            <div style={{ marginBottom: '12px' }}>{error}</div>
            <button onClick={fetchLeaderboard} style={{ border: 0, background: '#00d4aa', color: '#06110f', borderRadius: '8px', padding: '8px 18px', fontWeight: '700', cursor: 'pointer' }}>
              Retry
            </button>
          </div>
        )}

        {!loading && !error && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h2 style={{ margin: 0, fontSize: '15px', color: '#fff' }}>Top Players</h2>
              <span style={{ color: '#8d969d', fontSize: '11px' }}>Stake wins</span>
            </div>

            {leaderboard.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '55px 15px', border: '1px solid rgba(67, 88, 101, 0.66)', borderRadius: '14px', color: '#8d969d' }}>
                <div style={{ fontSize: '15px', fontWeight: '700', marginBottom: '6px' }}>No qualifying wins yet</div>
                <div style={{ fontSize: '13px' }}>Play verified Whot Stake matches to appear here.</div>
              </div>
            ) : (
              <div>
                {leaderboard.map((player) => {
                  const rank = Number(player.rank);
                  const style = rankStyle(rank);
                  return (
                    <div
                      key={player.userId}
                      style={{ display: 'grid', gridTemplateColumns: '44px 1fr auto', gap: '10px', alignItems: 'center', padding: rank <= 3 ? '14px 12px' : '11px 12px', marginBottom: '6px', borderRadius: '12px', background: style.background, border: style.border }}
                    >
                      <div style={{ textAlign: 'center', fontWeight: '800', fontSize: rank <= 3 ? '18px' : '13px', color: style.number }}>
                        {rank}
                      </div>

                      <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {player.avatarUrl ? (
                          <img src={player.avatarUrl} alt="" style={{ width: '38px', height: '38px', borderRadius: '50%', objectFit: 'cover', border: '1px solid rgba(67, 88, 101, 0.66)' }} />
                        ) : (
                          <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: '#111616', border: '1px solid rgba(67, 88, 101, 0.66)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9aa3ab', fontWeight: '700' }}>
                            {(player.username || '?').charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: '700', fontSize: '14px', color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {player.username || 'Unknown'}
                            {player.isVerified ? <span title="Verified" style={{ marginLeft: '5px', color: '#00d4aa' }}>✓</span> : null}
                          </div>
                          {player.rankBadge ? <div style={{ color: '#8d969d', fontSize: '10px', marginTop: '2px' }}>{player.rankBadge}</div> : null}
                        </div>
                      </div>

                      <div style={{ textAlign: 'right' }}>
                        <div style={{ color: '#00d4aa', fontWeight: '800', fontSize: '15px' }}>{Number(player.whotStakeWins || 0).toLocaleString()}</div>
                        <div style={{ color: '#8d969d', fontSize: '10px' }}>Wins</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {yourPosition && (
              <div style={{ marginTop: '18px', padding: '13px 14px', borderRadius: '12px', background: 'rgba(0, 212, 170, 0.08)', border: '1px solid rgba(0, 212, 170, 0.30)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                <div>
                  <div style={{ fontSize: '11px', color: '#9aa3ab', fontWeight: '700', textTransform: 'uppercase' }}>Your Position</div>
                  <div style={{ fontSize: '17px', fontWeight: '800', color: '#fff' }}>#{Number(yourPosition.rank).toLocaleString()}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: '#00d4aa', fontSize: '17px', fontWeight: '800' }}>{Number(yourPosition.whotStakeWins || 0).toLocaleString()}</div>
                  <div style={{ color: '#9aa3ab', fontSize: '10px' }}>Whot Stake Wins</div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <PrimaryNav />
    </div>
  );
}
